import React from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import {
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { formatIsoDuration } from "utils/dateUtils";
import CustomScrollbars from "../CustomScrollbars";
import ClientRunStatusChip from "./ClientRunStatusChip";
import RunTimestamp from "./RunTimestamp";
import TableSkeletonRows from "./TableSkeletonRows";
import { HEADER_CELL_SX, NO_WRAP_CELL_SX } from "./tableStyles";
import type { ClientRunHistoryItem } from "features/integrationsBeta/integrationsBeta";
import {
  timeHeaderSuffix,
  type TimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";

type Props = {
  rows: ClientRunHistoryItem[];
  isLoading: boolean;
  isFetching: boolean;
  selectedRunId?: number | null;
  onSelect: (row: ClientRunHistoryItem) => void;
  clientStatusLabel: (value: string | null | undefined) => string;
  timezoneMode: TimezoneDisplayMode;
  displayZone?: string;
  /** Internal users only: per-row jump to the run's internal drill-down. */
  onOpenInternalRun?: (runId: number) => void;
  /** Switches the empty state between "no runs" and "nothing matches". */
  hasFilters: boolean;
  pagination?: React.ReactNode;
};

/**
 * The run history as the client sees it (CAL-5776): when a run happened, how
 * long it took and its client-facing status - nothing internal, one row per
 * run (re-runs never reach it) and no sign of a status set by hand
 * (CAL-6165). Rows open the run's details with Calibrate's note.
 */
export default function ClientRunHistoryTable({
  rows,
  isLoading,
  isFetching,
  selectedRunId,
  onSelect,
  clientStatusLabel,
  timezoneMode,
  displayZone,
  onOpenInternalRun,
  hasFilters,
  pagination,
}: Props) {
  const timeSuffix = timeHeaderSuffix(timezoneMode);
  const timeLabel = (label: string) =>
    timeSuffix ? `${label} · ${timeSuffix}` : label;
  const showCellAbbreviation = timezoneMode === "client";
  const columnCount = onOpenInternalRun ? 5 : 4;

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 260,
        overflow: "clip",
      }}
    >
      <CustomScrollbars
        sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}
        trackOffsetTop={40}
      >
        <Table
          stickyHeader
          size="small"
          sx={{ minWidth: 640, borderColor: "divider" }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={HEADER_CELL_SX}>{timeLabel("Started")}</TableCell>
              <TableCell sx={HEADER_CELL_SX}>{timeLabel("Finished")}</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Duration</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Status</TableCell>
              {onOpenInternalRun && (
                <TableCell
                  sx={{ width: 48, p: 0.5 }}
                  aria-label="Internal run"
                />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows columns={columnCount} />
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  sx={{ py: 4, borderBottom: "none" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {hasFilters
                      ? "No runs match the current filters"
                      : "No runs yet"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  selected={selectedRunId === row.id}
                  onClick={() => onSelect(row)}
                  sx={{
                    cursor: "pointer",
                    opacity: isFetching ? 0.6 : 1,
                    "&:last-child td": { borderBottom: "none" },
                    "&:hover": { bgcolor: "calibrate.primarySoft06" },
                  }}
                >
                  <TableCell sx={NO_WRAP_CELL_SX}>
                    <RunTimestamp
                      iso={row.started_at}
                      timeZone={displayZone}
                      showAbbreviation={showCellAbbreviation}
                    />
                  </TableCell>
                  <TableCell sx={NO_WRAP_CELL_SX}>
                    {row.finished_at ? (
                      <RunTimestamp
                        iso={row.finished_at}
                        timeZone={displayZone}
                        showAbbreviation={showCellAbbreviation}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={NO_WRAP_CELL_SX}>
                    {formatIsoDuration(row.started_at, row.finished_at)}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ClientRunStatusChip
                        status={row.client_status}
                        label={clientStatusLabel(row.client_status)}
                      />
                      {row.note && (
                        <Tooltip title="Has a note from Calibrate">
                          <CommentOutlinedIcon
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  {onOpenInternalRun && (
                    <TableCell
                      align="right"
                      sx={{ width: 48, p: 0.5 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Open internal run">
                        <IconButton
                          size="small"
                          aria-label="Open internal run"
                          onClick={() => onOpenInternalRun(row.id)}
                        >
                          <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination}
    </Paper>
  );
}
